import { ParticipationFlowProvider, useParticipationFlow } from "~/components/participation/ParticipationFlow";
import { checkoutParticipationPolicy } from "./participation-policy";
import { EventRequestState } from "~/__generated__/graphql/graphql";
import { hasTicketApprovalProfile } from "./ApprovalCheckoutDialog";
import { PurchaserBookingStatus } from "./PurchaserBookingStatus";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useUser } from "~/hooks/use-user";
import { CheckoutSessionProvider, useCheckoutSession } from "./CheckoutSession";
import { useNavigate, useRouteLoaderData, useSearchParams } from "react-router";
import type { loader as confirmationLoader } from "~/routes/plans.$eventId.purchase-confirmation/route";
import type { loader as processingLoader } from "~/routes/plans.$eventId.purchase-processing/route";
import type { loader as followUpLoader } from "~/routes/plans.$eventId.your-going/route";
import type { EventFragment } from "~/__generated__/graphql/graphql";
import { PlanDialog } from "~/components/plans/components/PlanDialog";
import { trackLegacyCheckout as track } from "~/utils/analytics";
import { useRootEnvironment } from "~/root";
import { checkoutContactEmailSchema } from "~/utils/contact-email";
import { ApprovalCheckoutDialog } from "./ApprovalCheckoutDialog";
import { CheckoutDetailsStep, checkoutNameSchema } from "./CheckoutDetailsStep";
import { CheckoutEmailStep } from "./CheckoutEmailStep";
import { CheckoutPaymentStep } from "./CheckoutPaymentStep";
import { CheckoutTicketsStep } from "./CheckoutTicketsStep";
import { CheckoutStatusDialog } from "./CheckoutStatusDialog";
import { CheckoutTicketsSkeleton } from "./CheckoutTicketsSkeleton";
import { CheckoutGuestsStep } from "./CheckoutGuestsStep";
import { groupSelectionAvailable, namedGroupSelection } from "./group-checkout";
import { ApprovalQuestionsStep } from "./ApprovalQuestionsStep";
import { approvalAnswersComplete, approvalRequestForm } from "./approval-form";
import { getSelectedTicketVariation, TICKET_VARIATION_PARAM } from "./ticketVariations";
import { isTicketCheckoutAvailable } from "./ticketSalesAvailability";
import { purchaserBookingState } from "./purchaser-booking-state";

export type CheckoutStep = "tickets" | "guests" | "details" | "questions" | "email" | "payment";

const VALID_STEPS = new Set<CheckoutStep>(["tickets", "guests", "details", "questions", "email", "payment"]);

export const CHECKOUT_STEP_PARAM = "step";

export function isCheckoutStep(value: string | null): value is CheckoutStep {
  return value !== null && VALID_STEPS.has(value as CheckoutStep);
}

type Props = {
  plan: EventFragment;
  authenticated: boolean;
  userExists: boolean;
  userAnonymous: boolean;
  stripePublishableKey: string | undefined;
  ticketVariationsEnabled: boolean;
  onResumeChange?: (walletIntentID: string) => void;
};

export function CheckoutDialog(props: Props) {
  const { user } = useUser();
  return <CheckoutSessionProvider key={props.plan.id} eventId={props.plan.id} userId={user?.id}><CheckoutParticipation {...props}><CheckoutFlow {...props} /></CheckoutParticipation></CheckoutSessionProvider>;
}

function CheckoutParticipation({ children, ...props }: Props & { children: ReactNode }) {
  const checkout = useCheckoutSession();
  const { user } = useUser();
  const [search] = useSearchParams();
  const smsEnabled = useRootEnvironment()?.TICKET_OUTCOME_SMS_ENABLED;
  const approval = checkout.draft.attempt?.approvalRequired ?? props.plan.approveGuests;
  const request = props.plan.myRequest?.submittedAt ? props.plan.myRequest : null;
  const acceptedWaitlist = request?.state === EventRequestState.InAcceptedWaitlist;
  const retryable = request && [EventRequestState.Declined, EventRequestState.Expired, EventRequestState.InAcceptedWaitlist].includes(request.state);
  const group = checkout.draft.groupSlots.length > 1;
  const phoneComplete = props.authenticated && props.userExists && !!user?.phoneNumber?.trim();
  const policy = checkoutParticipationPolicy({
    approval, group, acceptedWaitlist,
    showingStatus: (approval && (!!props.plan.isAttending || !!request && (!retryable || search.get("step") === "status"))) || (search.get("step") === "status" && !!props.plan.myRegistration?.status.submittedAt),
    selectionAvailable: group
      ? props.ticketVariationsEnabled && !acceptedWaitlist && groupSelectionAvailable(checkout.draft.groupSlots, props.plan)
      : !props.ticketVariationsEnabled || !!getSelectedTicketVariation(props.plan, search.get(TICKET_VARIATION_PARAM)),
    guestsComplete: !!namedGroupSelection(checkout.draft.groupSlots, checkout.draft.purchaserSlotID),
    phoneComplete,
    profileComplete: hasTicketApprovalProfile(user),
    detailsComplete: phoneComplete && checkoutNameSchema.safeParse(user?.name).success,
    questionsRequired: props.plan.formInputs.length > 0,
    questionsComplete: approvalAnswersComplete(props.plan.formInputs, checkout.draft.answers, props.plan.myRequest),
    emailRequired: !smsEnabled && !checkout.draft.walletIntentID && !props.plan.isAttending && !checkoutContactEmailSchema.safeParse(user?.contactEmail).success,
  }, checkout.draft);
  return <ParticipationFlowProvider
    scope={checkout.scope}
    requestedStep={checkout.ready ? search.get("step") : null}
    policy={policy}
    facts={{ journey_kind: approval ? "approval" : "purchase", event_id: props.plan.id, is_group: group, ticket_count: Math.max(1, checkout.draft.groupSlots.length), approval_required: approval }}
    onNavigate={(step, replace) => checkout.setStep(step as Parameters<typeof checkout.setStep>[0], replace)}
    onClose={checkout.close}
  >{children}</ParticipationFlowProvider>;
}

function CheckoutFlow(props: Props) {
  const flow = useParticipationFlow();
  const checkout = useCheckoutSession();
  const { user } = useUser();
  const [search] = useSearchParams();
  const confirmation = useRouteLoaderData<typeof confirmationLoader>("routes/plans.$eventId.purchase-confirmation");
  const processing = useRouteLoaderData<typeof processingLoader>("routes/plans.$eventId.purchase-processing");
  const processingIntentID = processing && "walletIntentID" in processing ? processing.walletIntentID : null;
  const followUp = useRouteLoaderData<typeof followUpLoader>("routes/plans.$eventId.your-going");
  const followUpIntentID = followUp?.walletIntentID;
  const open = search.has("step");
  const { walletIntentID } = checkout.draft;
  const completed = !!walletIntentID && confirmation?.walletIntentID === walletIntentID;
  const { ready, complete, retainRecovery, close } = checkout;
  const registration = props.plan.myRegistration;
  const cancelledCurrentBooking = search.get("step") === "status" &&
    !search.has("registrationID") &&
    !!registration &&
    !!user?.id &&
    registration.purchaser?.id === user.id &&
    (registration.originalQuantity ?? 1) > 1 &&
    !!registration.status.submittedAt &&
    purchaserBookingState(registration) === "cancelled";
  const bookingWalletIntentID = registration?.purchaseWalletIntentID;
  useEffect(() => {
    if (completed) flow.complete("booking_confirmed");
    if (!ready || search.get("step") !== "status") return;
    if (registration && user?.id && registration.purchaser?.id === user.id) {
      const status = purchaserBookingState(registration);
      if (status === "confirmed") flow.complete("booking_confirmed");
      else if (status === "pending") flow.complete("request_sent");
    } else if (props.plan.myRequest?.submittedAt && props.plan.myRequest.state === EventRequestState.Pending) flow.complete("request_sent");
  }, [completed, ready, registration, search, user?.id, props.plan.myRequest, flow.complete]);
  useEffect(() => {
    if (!ready || !cancelledCurrentBooking) return;
    if (bookingWalletIntentID) complete(bookingWalletIntentID);
    close();
  }, [ready, cancelledCurrentBooking, bookingWalletIntentID, complete, close]);
  const onResumeChange = props.onResumeChange;
  useEffect(() => { onResumeChange?.(ready && !completed ? walletIntentID : ""); }, [ready, completed, walletIntentID, onResumeChange]);
  useEffect(() => {
    // Attendance alone cannot retire an attempt: it may still be a reservation,
    // and post-payment questions may be unfinished.
    if (ready && completed) complete(walletIntentID);
  }, [ready, completed, walletIntentID, complete]);
  useEffect(() => {
    if (ready && processingIntentID) retainRecovery(processingIntentID);
  }, [ready, processingIntentID, retainRecovery]);
  useEffect(() => {
    if (ready && followUpIntentID) retainRecovery(followUpIntentID, true);
  }, [ready, followUpIntentID, retainRecovery]);
  if (!open) return null;
  if (ready && cancelledCurrentBooking) return null;
  if (!ready && search.get("step") === "status" && !checkout.error) return <CheckoutStatusDialog onClose={checkout.close} />;
  if (!ready) return <PlanDialog onClose={checkout.close} fullScreenOnMobile>{checkout.error
    ? <p role="status" className="font-sans text-sm text-butter-green">{checkout.error}</p>
    : <CheckoutTicketsSkeleton plan={props.plan} label="Restoring checkout…" />}</PlanDialog>;
  if (search.get("step") === "status" && registration && !!user?.id && registration.purchaser?.id === user.id && (registration.originalQuantity ?? 1) > 1 && registration.status.submittedAt) {
    return <PlanDialog onClose={checkout.close}><PurchaserBookingStatus key={`${registration.id}:${user?.id}`} initial={registration} plan={props.plan} /></PlanDialog>;
  }
  return (
    <CheckoutRecovery key={checkout.scope} eventId={props.plan.id} plan={props.plan}>
      {(checkout.draft.attempt?.approvalRequired ?? props.plan.approveGuests) ? (
        <ApprovalCheckoutDialog
          plan={props.plan}
          authenticated={props.authenticated}
          userExists={props.userExists}
          stripePublishableKey={props.stripePublishableKey}
          ticketVariationsEnabled={props.ticketVariationsEnabled}
        />
      ) : <InstantCheckoutDialog {...props} />}
    </CheckoutRecovery>
  );
}

function CheckoutRecovery({ eventId, plan, children }: { eventId: string; plan: EventFragment; children: ReactNode }) {
  const flow = useParticipationFlow();
  const checkout = useCheckoutSession();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  // Check only the attempt present when checkout opens, not an attempt created by
  // the live payment form. Remounting Stripe fields mid-checkout would lose input.
  const [{ walletIntentID, paymentPhase, attempt }] = useState(checkout.draft);
  const [checking, setChecking] = useState(!!walletIntentID);
  const { canContinue, complete, update } = checkout;
  useEffect(() => {
    if (!walletIntentID) return;

    const resume = () => {
      if (paymentPhase === "processing" && !attempt && canContinue()) {
        void navigate(`/plans/${eventId}/purchase-processing?${new URLSearchParams({ walletIntentID })}`, { replace: true });
      } else {
        // The payment step resumes the saved command and checks Stripe before
        // exposing payment entry. A lost confirmation response may still need it.
        setChecking(false);
      }
    };
    const controller = new AbortController();
    const timeout = window.setTimeout(() => { controller.abort(); resume(); }, 10000);
    void fetch(`/api/events/${eventId}/checkout-status?${new URLSearchParams({ walletIntentID })}`, { signal: controller.signal })
      .then(response => response.ok ? response.json() : null)
      .then(result => {
        if (controller.signal.aborted) return;
        if (result?.destination && canContinue()) {
          if (result.walletIntentID === walletIntentID) {
            if (result.kind === "request") flow.complete("request_sent");
            if (result.kind === "confirmed") flow.complete("booking_confirmed");
          }
          if (result.kind === "request" && result.walletIntentID === walletIntentID) {
            complete(walletIntentID);
            setChecking(false);
          }
          void navigate(result.destination, { replace: true });
        } else if (result?.kind === "missing" && canContinue()) {
          complete(walletIntentID);
          setChecking(false);
        } else if (result?.kind === "failed" && result.walletIntentID === walletIntentID && canContinue()) {
          update({ walletIntentID: "", attempt: null, paymentPhase: "overview" });
          setChecking(false);
        } else {
          resume();
        }
      })
      .catch(() => {
        // A failed read is not a failed payment. The purchase command can still
        // recover the same attempt, without a fresh card confirmation.
        if (!controller.signal.aborted) resume();
      })
      .finally(() => window.clearTimeout(timeout));
    return () => { window.clearTimeout(timeout); controller.abort(); };
  }, [walletIntentID, paymentPhase, attempt, eventId, canContinue, complete, update, navigate]);
  if (!checking) return children;
  if (search.get("step") === "status") return <CheckoutStatusDialog onClose={checkout.close} />;
  return (
    <PlanDialog onClose={checkout.close} fullScreenOnMobile>
      <CheckoutTicketsSkeleton plan={plan} label="Checking your ticket…" />
    </PlanDialog>
  );
}

function InstantCheckoutDialog({
  plan,
  authenticated,
  userExists,
  stripePublishableKey,
  ticketVariationsEnabled,
}: Props) {
  const checkout = useCheckoutSession();
  const { user } = useUser();
  const ticketOutcomeSMSEnabled = useRootEnvironment()?.TICKET_OUTCOME_SMS_ENABLED;
  // Existing attempts must remain resumable when the delivery flag changes.
  const needsContactEmail =
    !ticketOutcomeSMSEnabled &&
    !checkout.draft.walletIntentID &&
    !plan.isAttending &&
    !checkoutContactEmailSchema.safeParse(user?.contactEmail).success;
  const hasDetails =
    authenticated &&
    userExists &&
    !!user?.phoneNumber?.trim() &&
    checkoutNameSchema.safeParse(user?.name).success;
  const needsQuestions = plan.formInputs.length > 0;
  const nextStep = !hasDetails ? "details" : needsQuestions ? "questions" : needsContactEmail ? "email" : "payment";
  const groupSlots = checkout.draft.groupSlots ?? [];
  const isGroup = groupSlots.length > 1;
  const [searchParams, setSearchParams] = useSearchParams();
  const stepParam = searchParams.get(CHECKOUT_STEP_PARAM);
  const step = isCheckoutStep(stepParam) ? stepParam : null;
  const checkoutAvailable = isTicketCheckoutAvailable(plan);
  const open = step !== null && checkoutAvailable;

  const checkoutWasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !checkoutWasOpenRef.current) {
      track("checkout_started", {
        event_id: plan.id,
        event_title: plan.title,
      });
    }
    checkoutWasOpenRef.current = open;
  }, [open, plan.id, plan.title]);

  useEffect(() => {
    if (step === null || checkoutAvailable) return;
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete(CHECKOUT_STEP_PARAM);
        next.delete("discountCode");
        next.delete(TICKET_VARIATION_PARAM);
        return next;
      },
      { replace: true, preventScrollReset: true },
    );
  }, [checkoutAvailable, setSearchParams, step]);

  const flow = useParticipationFlow();
  const displayedStep = flow.step;

  const close = () => {
    if (step !== null) {
      track("checkout_abandoned", {
        event_id: plan.id,
        event_title: plan.title,
        step: step === "questions" || step === "guests" ? "details" : step,
      });
    }
    checkout.close();
  };

  return (
    <PlanDialog open={open} onClose={close} fullScreenOnMobile>
      {checkout.error && <p role="alert" className="mb-3 font-sans text-sm text-butter-pink">{checkout.error} <button type="button" className="underline" onClick={() => void checkout.flush()}>Try again</button></p>}
      {displayedStep === "tickets" && (
        <CheckoutTicketsStep
          plan={plan}
          ticketVariationsEnabled={ticketVariationsEnabled}
          nextStep={isGroup ? "guests" : nextStep}
        />
      )}
      {displayedStep === "guests" && (
        <CheckoutGuestsStep plan={plan} onComplete={() => checkout.setStep(nextStep)} />
      )}
      {displayedStep === "details" && (
        <CheckoutDetailsStep
          plan={plan}
          authenticated={authenticated}
          userExists={userExists}
          onBack={flow.back}
        />
      )}
      {displayedStep === "email" && (
        <CheckoutEmailStep
          eventId={plan.id}
          onBack={flow.back}
        />
      )}
      {displayedStep === "questions" && (
        <ApprovalQuestionsStep
          plan={plan}
          showMessage={false}
          onBack={flow.back}
          onComplete={flow.next}
        />
      )}
      {displayedStep === "payment" && (
        <CheckoutPaymentStep
          onBack={flow.back}
          plan={plan}
          authenticated={authenticated}
          userExists={userExists}
          stripePublishableKey={stripePublishableKey}
          ticketVariationsEnabled={ticketVariationsEnabled}
          purchaserDetails={needsQuestions ? {
            form: approvalRequestForm(plan.formInputs, checkout.draft.answers, plan.myRequest),
          } : undefined}
        />
      )}
    </PlanDialog>
  );
}
